import { compareOrdersNewestFirst } from "./orderSorting";
import OrderDetails from "./OrderDetails";
import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Tabs,
  Tab,
  TableRow,
  TableCell,
  Typography,
  Tooltip,
} from "@mui/material";
import DataTable from "./DataTable";
import RowActions from "./RowActions";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import StatusBadge from "./StatusBadge";
import { formatCurrency, formatDate } from "./formatters";

export function parseOrderValue(value, fallback) {
  if (value == null) return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}
export function orderStatus(row) {
  return String(row.track_status || row.status || "unknown")
    .replace(/"/g, "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");
}
const groups = [
  "All Orders",
  "Pending",
  "Preparing",
  "Out for Delivery",
  "Completed",
  "Cancelled",
];
const statusGroup = (row) => {
  const status = orderStatus(row);
  if (["packing", "cooking", "accepted", "processing"].includes(status))
    return "preparing";
  if (["on the way", "dispatched"].includes(status)) return "out for delivery";
  if (status === "delivered") return "completed";
  if (["canceled", "rejected"].includes(status)) return "cancelled";
  return status;
};
const display = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};
export default function OrderTable({
  rows = [],
  loading,
  error,
  onRefresh,
  label = "Orders",
  type = "Online",
  getRowId = (row) => row.order_id ?? row.id,
  renderDetailsActions,
  onEdit,
  filters,
  renderRowActions,
  onViewOrder,
  newOrderCount = 0,
}) {
  const viewOrder = (row) => {
    setSelected(row);
    onViewOrder?.(row);
  };
  const [status, setStatus] = useState("All Orders");
  const [selected, setSelected] = useState(null);
  const [itemsOrder, setItemsOrder] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(7);
  const normalized = rows
    .filter(Boolean)
    .slice()
    .sort(compareOrdersNewestFirst)
    .map((row) => {
      const customer = parseOrderValue(row.customer_detail, {});
      const parsedItems = parseOrderValue(row.order_items ?? row.items, []);
      const itemList = Array.isArray(parsedItems)
        ? parsedItems
        : Array.isArray(parsedItems?.items)
        ? parsedItems.items
        : parsedItems && typeof parsedItems === "object"
        ? Object.values(parsedItems)
        : [];
      const itemGroups = new Map();
      itemList
        .filter((item) => item && typeof item === "object")
        .forEach((item) => {
          const key = JSON.stringify([
            item.item_id ??
              item.id ??
              item.name ??
              item.item_name ??
              item.itemName,
            item.item_category,
            item.item_type,
            item.price ?? item.item_price,
            item.notes ?? item.instructions,
          ]);
          const quantity = Number(item.quantity ?? item.qty ?? 1);
          const current = itemGroups.get(key);
          if (current)
            current.quantity += Number.isFinite(quantity) ? quantity : 1;
          else
            itemGroups.set(key, {
              ...item,
              quantity: Number.isFinite(quantity) ? quantity : 1,
            });
        });
      const items = [...itemGroups.values()];
      return {
        ...row,
        _customer:
          customer && typeof customer === "object" && !Array.isArray(customer)
            ? customer
            : {},
        _items: items,
        _name:
          customer?.name ||
          row.customer_name ||
          row.name ||
          row.adminName ||
          "No name provided",
        _phone:
          customer?.phone ||
          row.customer_phone ||
          row.contact_number ||
          row.customer_contact ||
          row.adminContact ||
          "—",
        _itemText: items.length
          ? items
              .map(
                (item) =>
                  `${
                    item.name || item.item_name || item.itemName || "Item"
                  } × ${item.quantity ?? item.qty ?? 1}`
              )
              .join(", ")
          : display(row.items ?? row.order_items),
        _id: row.tracking_id || row.order_id || row.id,
      };
    });
  const filtered = normalized.filter(
    (row) =>
      status === "All Orders" || statusGroup(row) === status.toLowerCase()
  );
  const detailOrder =
    selected &&
    (normalized.find((row) => getRowId(row) === getRowId(selected)) ||
      selected);
  const columns = [
    "#",
    "Order ID",
    "Customer",
    "Phone",
    "Items",
    "Total Amount",
    "Status",
    "Order Date & Time",
    "Action",
  ].map((headerName, index) => ({
    field: String(index),
    headerName,
    sortable: false,
  }));
  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        "& .MuiTable-root": { minWidth: 1120 },
        "& .MuiTableCell-head": { whiteSpace: "nowrap", fontSize: 12 },
        "& .MuiTableCell-root:last-child": { whiteSpace: "nowrap" },
      }}
    >
      {newOrderCount > 0 && (
        <Chip
          color="info"
          size="small"
          label={`${newOrderCount} new ${
            newOrderCount === 1 ? "order" : "orders"
          }`}
          sx={{ mb: 1 }}
        />
      )}
      <Tabs
        value={status}
        onChange={(_, next) => {
          setStatus(next);
          setPage(0);
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
          "& .MuiTab-root": { textTransform: "none", minHeight: 52 },
        }}
      >
        {groups.map((group) => (
          <Tab
            key={group}
            value={group}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{group}</span>
                <Chip
                  size="small"
                  label={
                    normalized.filter(
                      (row) =>
                        group === "All Orders" ||
                        statusGroup(row) === group.toLowerCase()
                    ).length
                  }
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Stack>
            }
          />
        ))}
      </Tabs>
      <DataTable
        label={label}
        data={filtered}
        columns={columns}
        loading={loading}
        error={error}
        onRefresh={onRefresh}
        filters={filters}
        getRowId={getRowId}
        searchFields={["_id", "_name", "_phone", "_itemText"]}
        pagination={{ page, pageSize }}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        initialPageSize={7}
        pageSizeOptions={[7, 10, 20, 50]}
        renderRow={(row) => (
          <TableRow
            hover
            sx={{ "& td": { py: 2, fontSize: 13, verticalAlign: "middle" } }}
          >
            <TableCell>
              {filtered.findIndex((item) => getRowId(item) === getRowId(row)) +
                1}
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {row._id}
              {row.isNew && (
                <Chip size="small" color="info" label="New" sx={{ ml: 1 }} />
              )}
            </TableCell>
            <TableCell sx={{ minWidth: 140 }}>
              <Typography variant="body2" fontWeight={600}>
                {row._name}
              </Typography>
              <Tooltip
                title={
                  row._customer.address ||
                  row.customer_address ||
                  row.address ||
                  (row.tableNumber ? `Table ${row.tableNumber}` : "")
                }
                arrow
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    maxWidth: 160,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row._customer.address ||
                    row.customer_address ||
                    row.address ||
                    (row.tableNumber ? `Table ${row.tableNumber}` : "")}
                </Typography>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{row._phone}</TableCell>
            <TableCell sx={{ minWidth: 170, maxWidth: 220 }}>
              <Typography variant="body2">
                {row._items.length
                  ? `${
                      row._items[0].name ||
                      row._items[0].item_name ||
                      row._items[0].itemName ||
                      "Item"
                    } × ${row._items[0].quantity ?? row._items[0].qty ?? 1}`
                  : row._itemText}
              </Typography>
              {row._items.length > 1 && (
                <Chip
                  component="button"
                  clickable
                  size="small"
                  label={`+${row._items.length - 1} more`}
                  onClick={() => setItemsOrder(row)}
                  sx={{ mt: 0.5, height: 20, border: 0, cursor: "pointer" }}
                />
              )}
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
              {row.total_price != null ? formatCurrency(row.total_price) : "—"}
            </TableCell>

            <TableCell>
              <StatusBadge status={orderStatus(row)} />
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>
              {row.created_at || row.createdAt
                ? formatDate(row.created_at || row.createdAt, true)
                : row.schedule || "—"}
            </TableCell>
            <TableCell>
              {renderRowActions ? (
                renderRowActions(row, () => viewOrder(row))
              ) : (
                <RowActions
                  variant="button"
                  label={`Actions for order ${row._id}`}
                  actions={[
                    {
                      label: "View",
                      icon: VisibilityOutlinedIcon,
                      onClick: () => viewOrder(row),
                    },
                  ]}
                />
              )}
            </TableCell>
          </TableRow>
        )}
      />
      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        aria-labelledby="order-details-title"
      >
        <DialogTitle id="order-details-title">
          Order details · {selected?._id}
        </DialogTitle>
        <DialogContent dividers>
          {detailOrder && (
            <>
              <OrderDetails order={detailOrder} type={type} />
              {renderDetailsActions && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Stack spacing={2}>{renderDetailsActions(detailOrder)}</Stack>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {onEdit && (
            <Button
              onClick={() => {
                onEdit(detailOrder);
                setSelected(null);
              }}
            >
              Edit order
            </Button>
          )}
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(itemsOrder)}
        onClose={() => setItemsOrder(null)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="order-items-title"
      >
        <DialogTitle id="order-items-title">
          Items · {itemsOrder?._id}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {itemsOrder?._items.slice(1).map((item, index) => (
              <Box
                key={`${item.id || item.item_id || item.name || index}`}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Typography>
                  {item.name || item.item_name || item.itemName || "Item"}
                </Typography>
                <Typography color="text.secondary">
                  × {item.quantity ?? item.qty ?? 1}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemsOrder(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
